import { Container, Graphics } from 'pixi.js';
import type { Game } from './Game';
import type { Scene } from './Scene';
import { clamp } from './math';

type SceneFactory = (game: Game) => Scene;

/**
 * Holds the active scene and performs fade transitions between scenes.
 * Transitions are requested with `change(factory)`; the actual swap happens
 * at the midpoint of the fade.
 */
export class SceneManager {
  private current: Scene | null = null;
  private readonly fade: Graphics;
  private readonly fadeLayer: Container;

  // Transition state.
  private transitioning = false;
  private pending: SceneFactory | null = null;
  private fadeAlpha = 0;
  private fadeDir = 0; // -1 fading out (to black), +1 fading in
  private readonly fadeSpeed = 3.2; // alpha per second

  constructor(private readonly game: Game) {
    this.fadeLayer = new Container();
    this.fadeLayer.eventMode = 'none';
    this.fade = new Graphics();
    this.fade.alpha = 0;
    this.fadeLayer.addChild(this.fade);
    this.game.app.stage.addChild(this.fadeLayer);
    this.drawFade();
  }

  /** Immediately set the first scene with no transition. */
  start(factory: SceneFactory): void {
    this.mount(factory);
  }

  /** Fade out, swap to the new scene, fade back in. */
  change(factory: SceneFactory): void {
    if (this.transitioning) {
      this.pending = factory;
      return;
    }
    this.pending = factory;
    this.transitioning = true;
    this.fadeDir = 1; // fade out (alpha 0 → 1)
    this.fadeLayer.eventMode = 'static'; // block input during transition
  }

  private mount(factory: SceneFactory): void {
    this.current?.exit();
    this.current?.root.destroy({ children: true });
    const scene = factory(this.game);
    this.current = scene;
    // Keep the fade layer on top.
    this.game.app.stage.addChildAt(scene.root, 0);
    scene.enter();
    scene.resize(this.game.width, this.game.height);
  }

  update(dt: number): void {
    if (this.transitioning) {
      this.fadeAlpha = clamp(this.fadeAlpha + this.fadeDir * this.fadeSpeed * dt, 0, 1);
      this.fade.alpha = this.fadeAlpha;

      if (this.fadeDir > 0 && this.fadeAlpha >= 1) {
        // Fully black: swap scenes, then fade back in.
        if (this.pending) this.mount(this.pending);
        this.pending = null;
        this.fadeDir = -1;
      } else if (this.fadeDir < 0 && this.fadeAlpha <= 0) {
        this.transitioning = false;
        this.fadeLayer.eventMode = 'none';
      }
    }

    this.current?.update(dt);
  }

  resize(w: number, h: number): void {
    this.drawFade();
    this.current?.resize(w, h);
  }

  private drawFade(): void {
    this.fade.clear();
    this.fade.rect(0, 0, this.game.width, this.game.height).fill(0x05040a);
  }
}
